import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router";
import { RouteFactory } from "@app/routePaths";
import { useFetchArticle } from "@routes/Article/hooks/useFetchArticle";
import { ArticlePaidModeType, VariantType } from "@routes/Article/utils/enums";
import {
  FeedArticleData,
  FeedNextSectionArticlesUrlPaths,
} from "@routes/Article/utils/types";
import { ARTICLE_INIFINITE_SCROLL_LIMIT } from "@util/constant";
import { checkIfFromPaidPressRelease } from "@util/helpers";
import axios from "axios";
import { includes } from "lodash-es";

type UseNextArticleProps = {
  data: FeedArticleData;
  lang?: string;
  /** Variant for A/B Test */
  variant?: string;
  /** Feature flag name for A/B Test */
  featureFlagName?: string;
};

export const useNextArticle = ({
  data,
  lang,
  variant,
  featureFlagName,
}: UseNextArticleProps) => {
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [moreStories, setMoreStories] = useState<FeedArticleData[]>([data]);

  const [moreStoriesUrlPaths, setMoreStoriesUrlPaths] = useState<
    FeedNextSectionArticlesUrlPaths[]
  >([]);
  const [tempFirstArticle, setTempFirstArticle] =
    useState<FeedArticleData | null>(null);

  const checkIfFetchMoreArticles = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!data) return;

    if (pathname.startsWith(RouteFactory.aseanBusiness)) return;
    if (pathname.startsWith(RouteFactory.globalEnterprise)) return;
    const userAgent = window.navigator.userAgent;
    if (includes(userAgent.toLowerCase(), "googlebot")) return;
    // Check if the article is from a paid press release.
    const isPaidPressRelease = checkIfFromPaidPressRelease(data?.sections);
    if (isPaidPressRelease) return;
    if (data.isPreview) return;

    return true;
  }, [pathname, data]);

  useEffect(() => {
    if (!data) return;
    if (moreStories.length > 1) return;
    if (data?.paidMode !== ArticlePaidModeType.PREMIUM) return;

    if (tempFirstArticle) return;

    const FetchFullContent = async () => {
      setIsLoading(true);
      const urlPath = data.urlPath.replace("/zh-hans", "");

      const response = await useFetchArticle({
        urlPath,
        lang,
      });

      setIsLoading(false);
      if (typeof response === "string") return;

      setMoreStories([response]);
      setTempFirstArticle(response);
    };

    FetchFullContent();
  }, [data, moreStories, tempFirstArticle, lang]);

  useEffect(() => {
    if (!checkIfFetchMoreArticles()) return;

    if (moreStoriesUrlPaths.length > 0) return;
    if (moreStories.length > 1) return;
    if (!variant) return;

    const firstArticleSectionName = data?.sections?.[0]?.uniqueName;
    const firstArticleId = data?.id;

    const fetchMoreUrlPaths = async () => {
      try {
        const nextArticleQuery = {
          section: firstArticleSectionName,
          excludeId: firstArticleId,
          size: ARTICLE_INIFINITE_SCROLL_LIMIT,
        };

        const chartbeatQuery = {
          urlPath: data.urlPath,
        };

        const isTestVariant =
          variant === `${VariantType.TEST}-${featureFlagName}`;

        const getUrl = isTestVariant
          ? "/_plat/api/v1/article-recommendation/chartbeat"
          : "/_plat/api/v1/articles/next-from-section";

        const query = isTestVariant ? chartbeatQuery : nextArticleQuery;

        const response = await axios.get<FeedNextSectionArticlesUrlPaths[]>(
          getUrl,
          { params: query }
        );

        const fetchedData = response.data;

        if (!fetchedData) return;

        setMoreStoriesUrlPaths(fetchedData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching next section articles:", error);
      }
    };

    fetchMoreUrlPaths();
  }, [
    data,
    moreStoriesUrlPaths,
    moreStories,
    variant,
    featureFlagName,
    checkIfFetchMoreArticles,
  ]);

  const FetchNextArticle = async () => {
    const articleIndex = moreStories.length - 1;

    setIsLoading(true);

    const urlPath = moreStoriesUrlPaths?.[articleIndex]?.urlPath;

    try {
      const response = await useFetchArticle({ urlPath, lang });
      if (typeof response === "string") throw new Error(response);

      setMoreStories((prev) => [...prev, response]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching next article:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const { ref } = useInView({
    triggerOnce: true,
    onChange: async (inView) => {
      if (!inView) return;
      if (!checkIfFetchMoreArticles()) return;
      if (moreStories.length >= ARTICLE_INIFINITE_SCROLL_LIMIT) return;
      if (moreStoriesUrlPaths.length === 0) return;

      FetchNextArticle();
    },
  });

  return {
    moreStories,
    isLoading,
    ref,
    moreStoriesUrlPaths,
  };
};
